﻿/** QuadriLateralFrustum (QLF) UV face indeces */
export const QLFFaceNdx = {
  front: 0,
  back: 1,
  left: 2,
  right: 3,
  top: 4,
  bottom: 5,
};

/**
 * Creates a 3D quadrilateral frustum shape (kind of a trapezoidal prism). The shape is created
 * using the BABYLON.MeshBuilder.CreatePolyhedron function. 4/7/2023.
 * This class was developed as a part of the prototyping of our mesh basket here:
 * https://playground.babylonjs.com/#WVLDLM#35
 */
export class QuadriLateralFrustumBuilder {
  /**
   * Creates and returns the mesh.
   * @param {string} name For display/debugging
   * @param {object} origin Object with properties (x, y, z) setting the of the left, bottom, front
   * position of the frustum.
   * @param {object} dimensions Object with properties (topWidth, botWidth, topDepth, botDepth, height).
   * @param {array} faceUV Each item in the faceUV array is a BABYLON.Vector4 object.
   * @param {array} faceColors Array of BABYLON.Color3
   * @param {array} invisibleFaces Array[6] of bool (front, back, left, right, top, bottom).
   * @param {object} scene Scene object
   * @returns {object} Mesh
   */
  createMesh(name, origin, dimensions, faceUV, faceColors, invisibleFaces, scene) {
    // Vertex positions on the X-axis:
    const xBotOffset = (dimensions.topWidth - dimensions.botWidth) / 2;
    const topXLeft = origin.x;
    const topXRight = origin.x + dimensions.topWidth;
    const botXLeft = origin.x + xBotOffset;
    const botXRight = origin.x + xBotOffset + dimensions.botWidth;

    // Vertex positions on the Y-axis:
    const topY = origin.y + dimensions.height;
    const botY = origin.y;

    // Vertex positions on the Z-axis:
    const zBotOffset = (dimensions.topDepth - dimensions.botDepth) / 2;
    const topZFront = origin.z;
    const topZBack = origin.z + dimensions.topDepth;
    const botZFront = origin.z + zBotOffset;
    const botZBack = origin.z + zBotOffset + dimensions.botDepth;

    const faces = [
      [0, 1, 2, 3], // 0: Front
      [7, 6, 5, 4], // 1: Back (counterclockwise)
      [3, 7, 4, 0], // 2: Left
      [1, 5, 6, 2], // 3: Right
      [4, 5, 1, 0], // 4: Top
      [3, 2, 6, 7], // 5: Bottom
    ];
    let hasInvisibleFaces = false;
    if (invisibleFaces && invisibleFaces.length > 0) {
      for (let i = 0; i < faces.length; i++) {
        if (invisibleFaces[i]) {
          faces[i] = [0, 0, 0, 0];
          hasInvisibleFaces = true;
        }
      }
    }
    const customOptions = {
      vertex: [
        [topXLeft, topY, topZFront],  // 0: left/top/front
        [topXRight, topY, topZFront], // 1: right/top/front
        [botXRight, botY, botZFront], // 2: right/bottom/front
        [botXLeft, botY, botZFront],  // 3: left/bottom/front
        [topXLeft, topY, topZBack],   // 4: left/top/back
        [topXRight, topY, topZBack],  // 5: right/top/back
        [botXRight, botY, botZBack],  // 6: right/bottom/back
        [botXLeft, botY, botZBack]    // 7: left/bottom/back
      ],
      face: faces
    };

    // If any of the faces are removed, then we need to render both sides:
    const orientation = hasInvisibleFaces ? BABYLON.Mesh.DOUBLESIDE : BABYLON.Mesh.DEFAULTSIDE;

    const options = {
      custom: customOptions,
      sideOrientation: orientation,
      faceUV: faceUV,
      faceColors: faceColors,
      //wrap: true,
    };
    return BABYLON.MeshBuilder.CreatePolyhedron(name || 'quadrilateral_frustum', options, scene);
  }
}
